import React from 'react';
import { Location } from 'history';

// Components
import ErrorBar from 'components/ErrorWrapper/ErrorBar';
import NoResults from 'components/NoResults/NoResults';

import NO_RESULTS from 'assets/no-results.svg';
import { whereAmI } from 'helpers/errorHelpers';
import CodedError from 'components/ErrorScreens/CodedError';
import { searchSplitter } from 'helpers/urlHelpers';

// Context
interface AppErrorContext {
    errors: Error[];
}
export const ErrorContext = React.createContext<AppErrorContext>({ errors: [] });

export interface Props {
    children: React.ReactNode;
    className?: string;
    location?: Location;
}

const defaultWrapperState: AppErrorContext = {
  errors: []
};

export default class AppErrorBoundary extends React.Component<Props, AppErrorContext> {
  constructor(props: Props) {
    super(props);
    this.state = { ...defaultWrapperState }
  }

  componentDidCatch(error: Error) {
    /* istanbul ignore next */
    if (window.newrelic) {
      const { location } = this.props;
      const newRelic = window.newrelic;
      newRelic.noticeError(
        error,
        {
          type: 'AppErrorBoundaryCaughtError',
          errorDetails: error,
          env: whereAmI(),
          location: location?.pathname
        }
      );
    }
    this.setState({ errors: this.state.errors.concat(error) });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.location && prevProps.location) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.clearErrors();
      }
    }
  }

    clearErrors = () => {
      this.setState({ ...this.state, errors: [] });
    };

    handleRetry = () => {
      if (window.location) {
        window.location.reload();
      }
    }

    render() {
      const { className, children } = this.props;
      const { errors } = this.state;

      const hasCode = !!(errors.length && window.location.search && searchSplitter(window.location.search)?.code);
      const showDefault = !!errors.length && !hasCode;
      const noErrors = !hasCode && !showDefault;

      return (
        <ErrorContext.Provider value={this.state}>
          <div className={className}>
            {!hasCode && errors.map((error) => {
              return <ErrorBar key={`error-${error.message}`} errorMessage={`${error.message},`} onRetry={this.handleRetry} />
            })}
            { hasCode && <CodedError />}
            { showDefault && <NoResults icon={NO_RESULTS} title='404 Not Found' /> }
            { noErrors && children }
          </div>
        </ErrorContext.Provider>
      );
    }
}
